import { useRef, useState } from "react";
import { ImagePlus, Camera, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { toast } from "sonner";

/**
 * Uploads photos to /files/upload. The default picker lets the user choose
 * camera OR gallery (no `capture` attr = OS default chooser). A secondary
 * shortcut opens the camera directly for quick "snap it now" flows.
 */
export default function PhotoUploader({ value = [], onChange, max = 4, testid = "photo-uploader" }) {
  const [uploading, setUploading] = useState(false);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const remaining = max - value.length;
    const list = Array.from(files).slice(0, remaining);
    if (Array.from(files).length > remaining) {
      toast.info(`Only ${remaining} more photo${remaining === 1 ? "" : "s"} can be added.`);
    }
    setUploading(true);
    const ids = [...value];
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(data.id);
      } catch (e) {
        toast.error("Upload failed. Try a smaller image.");
      }
    }
    setUploading(false);
    onChange(ids);
  };

  return (
    <div>
      <div className="grid grid-cols-4 gap-3">
        {value.map((id) => (
          <div key={id} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200">
            <AuthImage fileId={id} className="w-full h-full object-cover" />
            <button
              type="button"
              data-testid={`${testid}-remove-${id}`}
              onClick={() => onChange(value.filter((v) => v !== id))}
              className="absolute top-1 right-1 bg-white/95 hover:bg-white rounded-full p-1 shadow border border-slate-200"
            >
              <X className="w-3.5 h-3.5 text-slate-700" />
            </button>
          </div>
        ))}
        {value.length < max && (
          <button
            type="button"
            data-testid={`${testid}-add`}
            onClick={() => galleryRef.current?.click()}
            className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
            <span className="text-[11px] font-medium">Add photo</span>
          </button>
        )}
      </div>

      {value.length < max && (
        <div className="mt-2 flex items-center gap-3 text-[11px]">
          <button
            type="button"
            data-testid={`${testid}-camera`}
            onClick={() => cameraRef.current?.click()}
            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700 font-medium"
          >
            <Camera className="w-3.5 h-3.5" /> Use camera
          </button>
          <span className="text-slate-400">·</span>
          <span className="text-slate-500">Up to {max} photos. JPG/PNG, ≤10MB each.</span>
        </div>
      )}

      {/* Default: OS picker (gallery + files + camera). */}
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
      />
      {/* Explicit camera capture for the "Use camera" shortcut. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid={`${testid}-camera-input`}
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
      />
    </div>
  );
}
